import React from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Linking,
  Share,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';
import { moodBoardAPI, MoodBoard } from '../../api/mood_boards';

interface SocialShareModalProps {
  visible: boolean;
  onClose: () => void;
  moodBoard: {
    id: string;
    title: string;
    username: string;
    colors: string[];
    tags?: string[];
    images?: string[];
  };
  onShared?: (updated: MoodBoard) => void;
}

const SocialShareModal: React.FC<SocialShareModalProps> = ({
  visible,
  onClose,
  moodBoard,
  onShared,
}) => {
  const { colors } = useTheme();

  const shareMessage = `Check out this amazing mood board: "${moodBoard.title}" by @${moodBoard.username}\n\nColors: ${moodBoard.colors.join(', ')}\nTags: ${moodBoard.tags?.map((tag: string) => `#${tag}`).join(' ') || ''}\n\nCreated with Coloursense 🎨`;
  const shareUrl = `https://mycoloursense.com/moodboard/${moodBoard.id}`;

  const handleNativeShare = async () => {
    try {
      await Share.share({
        message: `${shareMessage}\n\n${shareUrl}`,
        url: shareUrl,
        title: `Mood Board: ${moodBoard.title}`,
      });
      try {
        const updated = await moodBoardAPI.share(Number(moodBoard.id));
        onShared?.(updated);
      } catch (e) {
        // Non-blocking
        console.warn('Failed to record share:', e);
      }
      onClose();
    } catch (error) {
      console.error('Error sharing:', error);
      Alert.alert('Error', 'Failed to share');
    }
  };

  const handleSocialShare = async (platform: string) => {
    try {
      let url = '';

      switch (platform) {
        case 'twitter': {
          const twitterText = encodeURIComponent(
            `${shareMessage}\n\n${shareUrl}`
          );
          url = `https://twitter.com/intent/tweet?text=${twitterText}`;
          break;
        }
        case 'facebook':
          url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
          break;
        case 'instagram':
          // Instagram doesn't support direct sharing via URL, so we'll copy to clipboard
          Alert.alert(
            'Instagram Share',
            'Copy the link and share it in your Instagram story or post!',
            [
              { text: 'Cancel', style: 'cancel' },
              {
                text: 'Copy Link',
                onPress: () => {
                  // Copy to clipboard functionality would go here
                  Alert.alert(
                    'Link Copied!',
                    'Paste it in your Instagram post'
                  );
                },
              },
            ]
          );
          return;
        case 'pinterest': {
          const pinterestDesc = encodeURIComponent(
            `${moodBoard.title} by @${moodBoard.username}`
          );
          url = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${pinterestDesc}`;
          break;
        }
        case 'linkedin':
          url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
          break;
        case 'whatsapp': {
          const whatsappText = encodeURIComponent(
            `${shareMessage}\n\n${shareUrl}`
          );
          url = `whatsapp://send?text=${whatsappText}`;
          break;
        }
        case 'telegram': {
          const telegramText = encodeURIComponent(
            `${shareMessage}\n\n${shareUrl}`
          );
          url = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${telegramText}`;
          break;
        }
        default:
          return;
      }

      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
        try {
          const updated = await moodBoardAPI.share(Number(moodBoard.id));
          onShared?.(updated);
        } catch (e) {
          console.warn('Failed to record share:', e);
        }
      } else {
        Alert.alert('Error', `${platform} is not installed on your device`);
      }
      onClose();
    } catch (error) {
      console.error(`Error sharing to ${platform}:`, error);
      Alert.alert('Error', `Failed to share to ${platform}`);
    }
  };

  const socialPlatforms = [
    {
      name: 'twitter',
      icon: 'logo-twitter',
      color: '#1DA1F2',
      label: 'Twitter',
    },
    {
      name: 'facebook',
      icon: 'logo-facebook',
      color: '#1877F2',
      label: 'Facebook',
    },
    {
      name: 'instagram',
      icon: 'logo-instagram',
      color: '#E4405F',
      label: 'Instagram',
    },
    {
      name: 'pinterest',
      icon: 'logo-pinterest',
      color: '#BD081C',
      label: 'Pinterest',
    },
    {
      name: 'linkedin',
      icon: 'logo-linkedin',
      color: '#0A66C2',
      label: 'LinkedIn',
    },
    {
      name: 'whatsapp',
      icon: 'logo-whatsapp',
      color: '#25D366',
      label: 'WhatsApp',
    },
    {
      name: 'telegram',
      icon: 'logo-telegram',
      color: '#0088CC',
      label: 'Telegram',
    },
  ];

  return (
    <Modal
      visible={visible}
      animationType={Platform.OS === 'web' ? 'fade' : 'slide'}
      presentationStyle={Platform.OS === 'web' ? undefined : 'pageSheet'}
      transparent={Platform.OS === 'web'}
      onRequestClose={onClose}
    >
      <View
        style={
          Platform.OS === 'web'
            ? styles.container
            : [styles.mobileContainer, { backgroundColor: colors.background }]
        }
      >
        <View
          style={[styles.modalWrapper, { backgroundColor: colors.background }]}
        >
          <View style={styles.header}>
            <Text style={[styles.title, { color: colors.text }]}>
              Share Mood Board
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          <View style={styles.content}>
            <Text style={[styles.subtitle, { color: colors.secondary }]}>
              Share &quot;{moodBoard.title}&quot; with your friends
            </Text>

            {/* Native Share Button */}
            <TouchableOpacity
              style={[
                styles.nativeShareButton,
                { backgroundColor: colors.primary },
              ]}
              onPress={handleNativeShare}
            >
              <Ionicons name="share-outline" size={20} color="#FFFFFF" />
              <Text style={styles.nativeShareText}>Share via System</Text>
            </TouchableOpacity>

            <View style={styles.divider}>
              <View
                style={[styles.dividerLine, { backgroundColor: colors.border }]}
              />
              <Text style={[styles.dividerText, { color: colors.secondary }]}>
                or share to
              </Text>
              <View
                style={[styles.dividerLine, { backgroundColor: colors.border }]}
              />
            </View>

            {/* Social Media Platforms */}
            <View style={styles.platformsGrid}>
              {socialPlatforms.map((platform) => (
                <TouchableOpacity
                  key={platform.name}
                  style={[
                    styles.platformButton,
                    { backgroundColor: colors.card },
                  ]}
                  onPress={() => handleSocialShare(platform.name)}
                >
                  <View
                    style={[
                      styles.platformIcon,
                      { backgroundColor: platform.color },
                    ]}
                  >
                    <Ionicons
                      name={
                        platform.icon as React.ComponentProps<
                          typeof Ionicons
                        >['name']
                      }
                      size={24}
                      color="#FFFFFF"
                    />
                  </View>
                  <Text style={[styles.platformLabel, { color: colors.text }]}>
                    {platform.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  mobileContainer: {
    flex: 1,
  },
  modalWrapper: {
    width: Platform.OS === 'web' ? '50%' : '100%',
    maxWidth: Platform.OS === 'web' ? 400 : undefined,
    borderRadius: Platform.OS === 'web' ? 16 : 0,
    shadowOffset: Platform.OS === 'web' ? { width: 0, height: 4 } : undefined,
    shadowOpacity: Platform.OS === 'web' ? 0.25 : undefined,
    shadowRadius: Platform.OS === 'web' ? 12 : undefined,
    elevation: Platform.OS === 'web' ? 8 : undefined,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Platform.OS === 'web' ? 20 : 16,
    paddingVertical: Platform.OS === 'web' ? 16 : 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
  },
  closeButton: {
    padding: 4,
  },
  content: {
    flex: 1,
    padding: Platform.OS === 'web' ? 20 : 16,
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
  },
  nativeShareButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    marginBottom: 24,
  },
  nativeShareText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  dividerLine: {
    flex: 1,
    height: 1,
  },
  dividerText: {
    fontSize: 14,
    marginHorizontal: 16,
  },
  platformsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Platform.OS === 'web' ? 12 : 8,
  },
  platformButton: {
    width: Platform.OS === 'web' ? '30%' : '45%',
    alignItems: 'center',
    paddingVertical: Platform.OS === 'web' ? 16 : 12,
    borderRadius: 12,
    shadowOffset: Platform.OS === 'web' ? { width: 0, height: 2 } : undefined,
    shadowOpacity: Platform.OS === 'web' ? 0.1 : undefined,
    shadowRadius: Platform.OS === 'web' ? 4 : undefined,
    elevation: Platform.OS === 'web' ? 2 : undefined,
  },
  platformIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  platformLabel: {
    fontSize: 12,
    fontWeight: '500',
    textAlign: 'center',
  },
});

export default SocialShareModal;
